import { useState, useEffect, useMemo } from 'react'
import Modal from '../shared/Modal'
import { Aircraft } from '../../types/intellijetnet'
import { AircraftService } from '../../services/intellijetnet/aircraft.service'
import { OffMarketService } from '../../services/off-market.service'

interface AircraftSearchModalProps {
  isOpen: boolean
  onClose: () => void
  modelId: number
  onSelectAircraft: (aircraft: Aircraft) => void
}

export default function AircraftSearchModal({ 
  isOpen, 
  onClose, 
  modelId, 
  onSelectAircraft 
}: AircraftSearchModalProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [aircraft, setAircraft] = useState<Aircraft[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [offMarketAircraftIds, setOffMarketAircraftIds] = useState<Set<number>>(new Set())

  // Fetch aircraft for the current model when modal opens
  useEffect(() => {
    if (isOpen && modelId) {
      fetchAircraft()
      fetchOffMarketAircraft()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modelId])

  const fetchAircraft = async () => {
    try {
      setLoading(true)
      setError(null)

      if (!modelId || isNaN(modelId)) {
        throw new Error('Invalid model ID provided')
      }

      const aircraftData = await AircraftService.getByModelId(modelId)

      if (!aircraftData || aircraftData.length === 0) {
        setError('No aircraft found for this model. Please check if the model has any aircraft in the database.')
        setAircraft([])
        return
      }

      setAircraft(aircraftData)
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to load aircraft data'
      setError(`Unable to load aircraft: ${errorMessage}. Please try again or contact support if the problem persists.`)
      setAircraft([])
    } finally {
      setLoading(false)
    }
  }

  const fetchOffMarketAircraft = async () => {
    try {
      if (!modelId || isNaN(modelId)) {
        return
      }

      const offMarketData = await OffMarketService.getByModelId(modelId)
      const aircraftIds = new Set(offMarketData.map(item => item.aircraft_id))
      setOffMarketAircraftIds(aircraftIds)
    } catch (err: any) {
      // Don't show error to user for this, as it's not critical for the search functionality
      // Just log it and continue with empty set
      setOffMarketAircraftIds(new Set())
    }
  }

  // Filter aircraft based on search term
  const filteredAircraft = useMemo(() => {
    if (!searchTerm.trim()) {
      return aircraft
    }

    const term = searchTerm.toLowerCase().trim()
    return aircraft.filter(item => 
      (item.sernbr && item.sernbr.toLowerCase().includes(term)) ||
      (item.regnbr && item.regnbr.toLowerCase().includes(term))
    )
  }, [aircraft, searchTerm])

  const handleSelectAircraft = (selectedAircraft: Aircraft) => {
    onSelectAircraft(selectedAircraft)
    onClose()
    setSearchTerm('')
  }

  const handleClose = () => {
    onClose()
    setSearchTerm('')
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Search Aircraft" size="large">
      <div className="space-y-4">
        <div className="text-sm text-gray-600">
          Search for aircraft by serial number or tail number to add to the market summary.
        </div>

        {/* Search Input */}
        <div>
          <label htmlFor="aircraft-search" className="block text-sm font-medium text-gray-700 mb-2">
            Search by Serial Number or Tail Number
          </label>
          <input
            id="aircraft-search"
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Enter serial number or tail number..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
            autoFocus
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-950"></div>
          </div>
        )}

        {/* Results */}
        {!loading && (
          <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-md">
            {filteredAircraft.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                {searchTerm.trim() ? 'No aircraft found matching your search.' : 'Enter a search term to find aircraft.'}
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredAircraft.map((item) => {
                  const isAlreadyInOffMarket = offMarketAircraftIds.has(item.aircraft_id)
                  
                  return (
                    <div
                      key={item.aircraft_id}
                      className={`px-4 py-3 hover:bg-gray-50 flex justify-between items-center ${
                        isAlreadyInOffMarket ? 'opacity-50' : ''
                      }`}
                    >
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {item.sernbr || 'No Serial Number'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {item.regnbr || 'No Registration'}
                        </div>
                        <div className="text-xs text-gray-400">
                          ID: {item.aircraft_id} | Year: {item.yearmfr || 'Unknown'}
                        </div>
                        {isAlreadyInOffMarket && (
                          <div className="text-xs text-orange-600 font-medium mt-1">
                            Already in off-market
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => handleSelectAircraft(item)}
                        disabled={isAlreadyInOffMarket}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                          isAlreadyInOffMarket
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-purple-950 text-white hover:bg-purple-900'
                        }`}
                      >
                        {isAlreadyInOffMarket ? 'Already Added' : 'Add'}
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  )
}
